import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEmbed } from "@/contexts/EmbedContext";
import HomePage from "@/pages/internal/home/HomePage";
import AgentWizardPage from "@/pages/internal/agentSkills/wizard/AgentWizardPage";
import BriefingWizard from "./briefing/BriefingWizard";
import { useBriefing } from "./briefing/useBriefing";

/**
 * ImplementationEntry — gate for the implementation home.
 *
 * For an account admin of an account not yet set up, onboarding is the
 * "Agentes do escritório" setup (it replaced the briefing and the kickoff
 * booking): the firm creates its agents itself. "Fazer depois" lands on the
 * home, where the agents card stays as the invitation. Creating the agents
 * writes a flow, so the next visit finds the account implemented and goes
 * home. Everyone else (agents, superadmins across accounts, set-up accounts)
 * gets the home.
 *
 * Embedded partner accounts keep the briefing for now: the setup navigates
 * with /internal paths the embed does not serve.
 *
 * The briefing query is only enabled for account admins so non-admin visits
 * don't get-or-create briefing rows.
 */
export default function ImplementationEntry() {
  const { clientId: paramId } = useParams();
  const { user, isAdmin, isSuperAdmin, markOnboarding } = useAuth();
  const { isEmbed } = useEmbed();
  const clientId = paramId || user?.accountId;

  const isAccountAdmin =
    !!user && typeof isAdmin === "function" && isAdmin() && !isSuperAdmin?.();

  const { briefing, isLoading } = useBriefing(isAccountAdmin ? clientId : null);

  if (isAccountAdmin && isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show the wizard only for a genuinely-new account. An account already live
  // (working flow OR active channel) that never ran the briefing — e.g. set up
  // before the wizard shipped — keeps the dashboard instead of being forced
  // into onboarding. The backend persists that detection on the briefing
  // (status "completed", completedBy "system:*"), so this can never re-arm.
  const showWizard =
    isAccountAdmin &&
    !!briefing &&
    briefing.status !== "completed" &&
    !briefing.alreadyImplemented;

  if (showWizard && isEmbed) {
    return <BriefingWizard clientId={clientId} />;
  }
  if (showWizard && !user?.onboardingProgress?.agentsSetupDeferred) {
    return <AgentWizardPage accountId={clientId} onDefer={() => markOnboarding({ agentsSetupDeferred: true })} />;
  }

  // Completed briefing → the Home v2 front door, which replaced the legacy
  // implementation dashboard.
  //
  // Two dashboard-era companions were dropped with it. TourProvider existed so
  // the old dashboard's useTour() kept working, and its spotlight branches were
  // already inert. TutorialsLauncher was a floating launcher whose three
  // tutorials were hardcoded in the component and duplicated CMS articles that
  // already exist — the ask box and the editorial row now cover that ground
  // from a single source of truth. The per-user inbox tutorial is unaffected;
  // it runs from InboxTour on the inbox's first visit.
  return <HomePage />;
}
